import { ColDef, ColumnState, ValueGetterParams, ValueSetterParams } from "ag-grid-community";
import { aggridHeaderClass } from "src/components/common/common";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { getDefaultRowIndexColumnConfig } from "src/utils/grid/gridStyleUtils";
import {
	MonthDayColumn,
	createMonthDayColumnMeta,
	formatDayColId,
	getDaysInMonth,
} from "src/utils/date/dateUtils";
import { isValidOxCellInput } from "src/utils/string/stringUtil";
import { showWarning } from "src/utils/toast/toastUtil";

import { EQM2070_R01DetailPivotDto } from "./Dto_EQM2070_R01";
import { normalizeOxCellValue } from "./Util_EQM2070_R01";

export const DETAIL_COLUMN_STATE_KEY = "EQM2070_R01-detail-grid-column-state-v6";

/** EQM2070 기본 왼쪽 틀 고정 대상 colId */
export const EQM2070_DEFAULT_PINNED_COL_IDS = [
	"rowIndex",
	"eqmType",
	"inspectNm",
] as const;

/** 상세 그리드 컬럼 ID → 헤더명 매핑 */
export const headerNames_EQM2070_R01Detail: Record<string, string> = {
	rowIndex: "",
	eqmType: "설비유형",
	inspectCd: "점검코드",
	inspectNm: "점검명",
};

/** 점검년월 기준 1일~말일 동적 컬럼 목록 생성 */
export function getMonthDayColumns_EQM2070_R01(inspectDtYm: string): MonthDayColumn[] {
	const [year, month] = inspectDtYm.split("-");
	if (!year || !month) return [];
	const days = getDaysInMonth(inspectDtYm);
	return Array.from({ length: days }, (_, index) => {
		const day = index + 1;
		const dayDd = String(day).padStart(2, "0");
		const dayMeta = createMonthDayColumnMeta(day, inspectDtYm);
		return {
			...dayMeta,
			dayDd,
			colId: formatDayColId(day),
		};
	});
}

/** 단일 헤더(N일) 일자 컬럼 ColDef 생성 */
export function buildMonthDayColumnDefs(
	inspectDtYm: string,
): ColDef<EQM2070_R01DetailPivotDto>[] {
	const createDaySetter =
		(dayDd: string) =>
		(params: ValueSetterParams<EQM2070_R01DetailPivotDto, string>) => {
			if (!params.data) return false;
			if (!isValidOxCellInput(params.newValue)) {
				showWarning("O, X 형식의 텍스트만 입력가능합니다");
				return false;
			}
			const nextValue = normalizeOxCellValue(params.newValue);
			const currentMap = params.data.dayValues;
			if ((currentMap?.[dayDd] ?? "") === nextValue) return false;
			if (!params.data.dayValues) {
				params.data.dayValues = {};
			}
			// paddedGridData는 행 객체를 shallow copy하므로 dayValues 객체 참조를 유지한 채 in-place 갱신해야 store와 동기화된다.
			params.data.dayValues[dayDd] = nextValue;
			return true;
		};

	return getMonthDayColumns_EQM2070_R01(inspectDtYm).flatMap((column) => {
		if (!column.colId || !column.dayDd) return [];
		return [
			{
				headerName: column.headerName,
				colId: column.colId,
				field: column.colId as keyof EQM2070_R01DetailPivotDto & string,
				headerClass: column.headerClass,
				width: 75,
				editable: true,
				cellStyle: { textAlign: "center" },
				valueGetter: (params: ValueGetterParams<EQM2070_R01DetailPivotDto>) =>
					params.data?.dayValues?.[column.dayDd!] ?? "",
				valueSetter: createDaySetter(column.dayDd),
			},
		];
	});
}

/**
 * 상세 pivot 그리드 기본 컬럼 정의
 */
export const createDetailBaseColumnDefs = (
	inspectDtYm: string,
): ColDef<EQM2070_R01DetailPivotDto>[] => {
	const dayColumnDefs = buildMonthDayColumnDefs(inspectDtYm);

	return [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: "설비유형", colId: "eqmType", field: "eqmTypeNm", width: 100,
			editable: false,
			suppressMovable: true,
			valueGetter: (params) => params.data?.eqmTypeNm || params.data?.eqmType || "",
		},
		{
			headerName: "점검명", colId: "inspectNm", field: "inspectNm", width: 200,
			minWidth: 120,
			editable: false,
			suppressMovable: true,
		},
		...dayColumnDefs,
	];
};

export const createDetailDefaultColumnState = (
	baseColumnDefs: ColDef<EQM2070_R01DetailPivotDto>[],
) => buildDefaultColumnStateFromDefs(baseColumnDefs, [...EQM2070_DEFAULT_PINNED_COL_IDS]);

/**
 * 상세 pivot 그리드 최종 컬럼 정의 (컬럼 상태 병합 포함)
 */
export const createDetailColumnDefs = (
	baseColumnDefs: ColDef<EQM2070_R01DetailPivotDto>[],
	columnState: ColumnState[] | null,
): ColDef<EQM2070_R01DetailPivotDto>[] => {
	if (columnState === null) return baseColumnDefs;
	return GridCustomColumnDefs<EQM2070_R01DetailPivotDto>({
		baseColumnDefs,
		columnState,
		headerNames: headerNames_EQM2070_R01Detail,
		fallbackHeaderClass: aggridHeaderClass,
		mergePinnedFromState: false,
	});
};
